import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, Stack, Link } from "@mui/material";
import { makeStyles } from "@mui/styles";
import berlinCity from "../../../images/berlin.png";
import stuttgartCity from "../../../images/stuttgart.png";
import barcelonaCity from "../../../images/barcelona.png";
import warsawCity from "../../../images/warsaw.png";
import londonCity from "../../../images/london.png";
import athensCity from "../../../images/athens.png";
import { intelligenceSelectors } from "../../../slices/Intelligence/IntelligenceSlice";
import { getDefaultCities } from "../../../actions/intelligence";
import { setCities } from "../../../slices/Intelligence/CostOfLivingSlice";
import { getCostOfLiving } from "../../../actions/intelligence/CostOfLiving";

const useStyles = makeStyles(() => ({
    root: {
        justifyContent: "center",
        textAlign: "center",
    },
    text: {
        color: "#7F7F7F",
        fontSize: 11,
    },
    boldText: {
        fontWeight: 500,
    },
    header: {
        textAlign: "left",
        paddingLeft: 5,
        paddingBottom: 2,
        color: "#7F7F7F",
        fontSize: 14,
        fontWeight: 400,
    },
    universitiesText: {
        textAlign: "left",
        paddingLeft: 7,
        fontSize: 11,
    },
    universitiesSubText: {
        textAlign: "left",
        paddingLeft: 7,
        fontWeight: 500,
        fontSize: 12,
    },
    textBox: {
        paddingTop: 140,
    },
    leftBox: {
        backgroundPosition: "center",
        width: 90,
        height: 180,
        backgroundSize: "cover",
        borderTopLeftRadius: 4,
        borderBottomLeftRadius: 4,
    },
    rightBox: {
        backgroundPosition: "center",
        width: 90,
        height: 180,
        backgroundSize: "cover",
        borderTopRightRadius: 4,
        borderBottomRightRadius: 4,
    },
    link: {
        cursor: "pointer",
        textDecoration: "none",
        color: "inherit",
    },
}));

export default function NoContent() {
    const defaultCities = useSelector(intelligenceSelectors.defaultCities);
    const dispatch = useDispatch();
    const classes = useStyles();

    useEffect(() => {
        if (defaultCities) return;
        dispatch(getDefaultCities());
    }, []);

    const handleLinkClicked = (linkIndex) => {
        let currentCity;
        let comparisonCity;
        if (linkIndex === 0) {
            currentCity = defaultCities.find((city) => city.name === "Berlin");
            comparisonCity = defaultCities.find((city) => city.name === "Stuttgart");
        } else if (linkIndex === 1) {
            currentCity = defaultCities.find((city) => city.name === "Barcelona");
            comparisonCity = defaultCities.find((city) => city.name === "Warsaw");
        } else if (linkIndex === 2) {
            currentCity = defaultCities.find((city) => city.name === "London");
            comparisonCity = defaultCities.find((city) => city.name === "Athens");
        }

        dispatch(setCities({ currentCity, comparisonCity }));
        dispatch(getCostOfLiving({ currentCityId: currentCity.id, comparisonCityId: comparisonCity.id }));
    };

    return (
        <Box className={classes.root}>
            <Typography variant="h4" className={classes.typographyPrimary}>
                Let's Start Comparing The Cost Of Living
            </Typography>
            <Typography className={classes.typographyPrimary}>
                Please select cities in the menu above comparing them
            </Typography>
            <Typography pt={1} className={classes.text}>
                OR
            </Typography>
            <Typography pt={1} pb={6} className={classes.boldText}>
                start by selecting one of the suggested choices:
            </Typography>
            <Stack>
                <Typography className={classes.header}>City</Typography>
                <Stack width="100%" direction="row" mb={1} justifyContent="center">
                    <Link className={classes.link} onClick={() => handleLinkClicked(0)}>
                        <Stack direction="row">
                            <Stack
                                style={{
                                    backgroundImage: `linear-gradient(0deg, rgba(255, 255, 255, 0.9) 22.22%, rgba(255, 255, 255, 0) 100%), url(${berlinCity})`,
                                }}
                                className={classes.leftBox}
                            >
                                <Stack className={classes.textBox}>
                                    <Typography className={classes.universitiesSubText}>Berlin</Typography>
                                    <Typography color="text.secondary" className={classes.universitiesText}>
                                        Germany
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Stack
                                style={{
                                    backgroundImage: `linear-gradient(0deg, rgba(255, 255, 255, 0.9) 22.22%, rgba(255, 255, 255, 0) 100%), url(${stuttgartCity})`,
                                }}
                                className={classes.rightBox}
                            >
                                <Stack className={classes.textBox}>
                                    <Typography className={classes.universitiesSubText}>Stuttgart</Typography>
                                    <Typography color="text.secondary" className={classes.universitiesText}>
                                        Germany
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Link>
                    <Stack pl={1}>
                        <Link className={classes.link} onClick={() => handleLinkClicked(1)}>
                            <Stack direction="row">
                                <Stack
                                    style={{
                                        backgroundImage: `linear-gradient(0deg, rgba(255, 255, 255, 0.9) 22.22%, rgba(255, 255, 255, 0) 100%), url(${barcelonaCity})`,
                                    }}
                                    className={classes.leftBox}
                                >
                                    <Stack className={classes.textBox}>
                                        <Typography className={classes.universitiesSubText}>Barcelona</Typography>
                                        <Typography color="text.secondary" className={classes.universitiesText}>
                                            Spain
                                        </Typography>
                                    </Stack>
                                </Stack>
                                <Stack
                                    style={{
                                        backgroundImage: `linear-gradient(0deg, rgba(255, 255, 255, 0.9) 22.22%, rgba(255, 255, 255, 0) 100%), url(${warsawCity})`,
                                    }}
                                    className={classes.rightBox}
                                >
                                    <Stack className={classes.textBox}>
                                        <Typography className={classes.universitiesSubText}>Warsaw</Typography>
                                        <Typography color="text.secondary" className={classes.universitiesText}>
                                            Poland
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Link>
                    </Stack>
                    <Stack pl={1}>
                        <Link className={classes.link} onClick={() => handleLinkClicked(2)}>
                            <Stack direction="row">
                                <Stack
                                    style={{
                                        backgroundImage: `linear-gradient(0deg, rgba(255, 255, 255, 0.9) 22.22%, rgba(255, 255, 255, 0) 100%), url(${londonCity})`,
                                    }}
                                    className={classes.leftBox}
                                >
                                    <Stack className={classes.textBox}>
                                        <Typography className={classes.universitiesSubText}>London</Typography>
                                        <Typography color="text.secondary" className={classes.universitiesText}>
                                            United Kingdom
                                        </Typography>
                                    </Stack>
                                </Stack>
                                <Stack
                                    style={{
                                        backgroundImage: `linear-gradient(0deg, rgba(255, 255, 255, 0.9) 22.22%, rgba(255, 255, 255, 0) 100%), url(${athensCity})`,
                                    }}
                                    className={classes.rightBox}
                                >
                                    <Stack className={classes.textBox}>
                                        <Typography className={classes.universitiesSubText}>Athens</Typography>
                                        <Typography color="text.secondary" className={classes.universitiesText}>
                                            Greece
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Link>
                    </Stack>
                </Stack>
            </Stack>
        </Box>
    );
}
